import React, { useState, useEffect, useRef } from 'react';
import './DailyProgressTracker.css';
import { getAoeDayKey } from '../utils/dailyUtils';

const TIERS = [
  { id: 'bronze', name: 'Bronze', limit: 500, color: '#cd7f32', ringColor: '#cd7f32', icon: '🥉' },
  { id: 'silver', name: 'Silver', limit: 2500, color: '#e0e0e0', ringColor: '#00bcd4', icon: '🥈' },
  { id: 'gold', name: 'Gold', limit: 10000, color: '#ffd700', ringColor: '#ffd700', icon: '🥇' }
];

const DailyProgressTracker = ({
  dailyClicks = 0,
  onGoalCompleted = null,
  isClicking = false,
  currentStreak = 0,
  streakFreezes = 0,
  onShare = null
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [celebration, setCelebration] = useState(null); // { icon, title, subtext }
  const [displayedClicks, setDisplayedClicks] = useState(dailyClicks);
  const [animatedProgress, setAnimatedProgress] = useState(0);

  const animationRef = useRef(null);
  const collapseTimerRef = useRef(null);
  // Tracks the last dailyClicks value we animated to (so we know the delta on next burst)
  const lastShownClicksRef = useRef(dailyClicks);
  // Tracks current displayedClicks for the animation start point (avoids stale closure)
  const displayedClicksRef = useRef(dailyClicks);
  // Skip animation on first load — just sync silently
  const isInitialLoadRef = useRef(true);
  const tiersCompletedRef = useRef({ bronze: false, silver: false, gold: false });

  const getCurrentTierInfo = (clicks) => {
    if (clicks < TIERS[0].limit) return { current: TIERS[0], prevLimit: 0, index: 0 };
    if (clicks < TIERS[1].limit) return { current: TIERS[1], prevLimit: TIERS[0].limit, index: 1 };
    return { current: TIERS[2], prevLimit: TIERS[1].limit, index: 2 };
  };

  const getProgressForClicks = (clicks) => {
    const { current, prevLimit } = getCurrentTierInfo(clicks);
    const tierRange = current.limit - prevLimit;
    return Math.min(Math.max((clicks - prevLimit) / tierRange, 0), 1) * 100;
  };

  const isMaxed = dailyClicks >= TIERS[2].limit;
  const perfectWeeks = Math.floor(currentStreak / 7);

  // Keep displayedClicksRef in sync so the animation closure always has the live value
  useEffect(() => {
    displayedClicksRef.current = displayedClicks;
  }, [displayedClicks]);

  // Cancel in-flight animation when clicking starts (tracker is about to hide)
  useEffect(() => {
    if (isClicking) {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current);
    }
  }, [isClicking]);

  // Main animation effect: runs when dailyClicks changes OR when clicking stops
  useEffect(() => {
    // First load: sync silently, no animation
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      setDisplayedClicks(dailyClicks);
      setAnimatedProgress(getProgressForClicks(dailyClicks));
      lastShownClicksRef.current = dailyClicks;
      return;
    }

    // While clicking: skip — we'll catch up when clicking stops
    if (isClicking) return;

    const endValue = dailyClicks;
    if (endValue === lastShownClicksRef.current) return;

    const startValue = displayedClicksRef.current;
    lastShownClicksRef.current = endValue;

    if (endValue > startValue) {
      // New clicks arrived — cancel existing animation, expand, and roll numbers
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current);

      setIsExpanded(true);

      // Duration scales with delta: bigger bursts animate longer (max 900ms)
      const duration = Math.min(900, Math.max(300, (endValue - startValue) * 2));
      const animStart = Date.now();
      const capturedStart = startValue;

      const animate = () => {
        const elapsed = Date.now() - animStart;
        const ratio = Math.min(elapsed / duration, 1);
        const easeOut = 1 - Math.pow(1 - ratio, 3);
        const current = capturedStart + (endValue - capturedStart) * easeOut;

        setDisplayedClicks(Math.floor(current));
        setAnimatedProgress(getProgressForClicks(current));

        if (ratio < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          setDisplayedClicks(endValue);
          setAnimatedProgress(getProgressForClicks(endValue));
          // Show the expanded panel for a beat, then collapse
          collapseTimerRef.current = setTimeout(() => {
            setIsExpanded(false);
          }, 2500);
        }
      };

      animate();
    } else {
      // Daily reset or correction — sync without animation
      setDisplayedClicks(endValue);
      setAnimatedProgress(getProgressForClicks(endValue));
    }
  }, [dailyClicks, isClicking]); // eslint-disable-line react-hooks/exhaustive-deps

  // Celebration logic
  const showCelebrationFor = ({ icon, title, subtext }) => {
    setCelebration({ icon, title, subtext });
    setTimeout(() => setCelebration(null), 3000);
  };

  // Tier completion celebrations (once per tier per day)
  useEffect(() => {
    TIERS.forEach(tier => {
      if (dailyClicks >= tier.limit && !tiersCompletedRef.current[tier.id]) {
        tiersCompletedRef.current[tier.id] = true;
        const key = `dailyCelebration_${getAoeDayKey()}_${tier.id}`;
        if (!localStorage.getItem(key)) {
          showCelebrationFor({
            icon: tier.icon,
            title: `${tier.name} Goal Complete!`,
            subtext: tier.id === 'gold' ? "Incredible! You've mastered today!" : 'Keep going for the next tier!'
          });
          localStorage.setItem(key, 'true');
          if (onGoalCompleted && tier.id === 'bronze') onGoalCompleted();
        }
      } else if (dailyClicks < tier.limit) {
        tiersCompletedRef.current[tier.id] = false;
      }
    });
  }, [dailyClicks]); // eslint-disable-line react-hooks/exhaustive-deps

  // Perfect week celebrations (once per milestone)
  useEffect(() => {
    if (currentStreak > 0 && currentStreak % 7 === 0) {
      const weekNum = Math.floor(currentStreak / 7);
      const key = `perfectWeek_${weekNum}`;
      if (!localStorage.getItem(key)) {
        showCelebrationFor({
          icon: '🏆',
          title: `${weekNum} Perfect Week${weekNum !== 1 ? 's' : ''}!`,
          subtext: `${currentStreak} days in a row — incredible!`
        });
        localStorage.setItem(key, 'true');
      }
    }
  }, [currentStreak]);

  const handleStreakShare = (e) => {
    e.stopPropagation();
    if (onShare && currentStreak > 0) {
      onShare(`I'm on a ${currentStreak}-day streak on Click Mania!\nPlay now: https://clickmania.app`, 'streak');
    }
  };

  const handleManualToggle = () => {
    if (isMaxed && !isExpanded) {
      showCelebrationFor({
        icon: TIERS[2].icon,
        title: 'Gold Goal Complete!',
        subtext: "Incredible! You've mastered today!"
      });
    }
    if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current);
    setIsExpanded(prev => !prev);
  };

  const { current: displayTier, index: tierIndex } = getCurrentTierInfo(displayedClicks);
  const progressColor = isMaxed ? '#4ade80' : displayTier.ringColor;
  const trackColor = 'rgba(255, 255, 255, 0.2)';

  return (
    <div className={`daily-progress-tracker ${isClicking ? 'hidden-during-clicking' : ''}`}>
      {/* Celebration overlay */}
      {celebration && (
        <div className="celebration-overlay">
          <div className="celebration-content">
            <div className="celebration-emoji">{celebration.icon}</div>
            <div className="celebration-text">{celebration.title}</div>
            <div className="celebration-subtext">{celebration.subtext}</div>
          </div>
        </div>
      )}

      <div
        className={`tracker-container ${isExpanded ? 'expanded' : 'compact'}`}
        onClick={handleManualToggle}
        style={{ '--tier-color': displayTier.ringColor }}
      >
        {/* Compact View - Calendar Icon */}
        <div className={`compact-view ${isExpanded ? 'hidden' : 'visible'}`}>
          <div className={`calendar-icon ${isMaxed ? 'completed' : ''}`}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="4" width="18" height="16" rx="2" fill="url(#calendarGrad)" stroke="rgba(255,255,255,0.3)" strokeWidth="1"/>
              <rect x="3" y="4" width="18" height="4" rx="2" fill={displayTier.color}/>
              <circle cx="7" cy="2" r="1" fill="#f1c40f"/>
              <circle cx="17" cy="2" r="1" fill="#f1c40f"/>
              <line x1="7" y1="1" x2="7" y2="6" stroke="#f1c40f" strokeWidth="2" strokeLinecap="round"/>
              <line x1="17" y1="1" x2="17" y2="6" stroke="#f1c40f" strokeWidth="2" strokeLinecap="round"/>
              <text x="12" y="15" textAnchor="middle" fontSize="10" fill="white">
                {displayedClicks >= TIERS[0].limit ? (tierIndex >= 1 ? TIERS[tierIndex].icon : TIERS[0].icon) : ''}
              </text>
              <defs>
                <linearGradient id="calendarGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="rgba(59, 130, 246, 0.3)"/>
                  <stop offset="100%" stopColor="rgba(147, 51, 234, 0.3)"/>
                </linearGradient>
              </defs>
            </svg>
            {isMaxed && <div className="completion-check">🎉</div>}
          </div>

          {/* Progress ring */}
          <div className="progress-ring">
            <svg className="progress-ring-svg" width="32" height="32">
              <circle
                className="progress-ring-circle-bg"
                stroke={trackColor}
                strokeWidth="2"
                fill="transparent"
                r="14"
                cx="16"
                cy="16"
              />
              <circle
                className="progress-ring-circle"
                stroke={progressColor}
                strokeWidth="2.5"
                fill="transparent"
                r="14"
                cx="16"
                cy="16"
                strokeLinecap="round"
                style={{
                  strokeDasharray: `${2 * Math.PI * 14}`,
                  strokeDashoffset: `${2 * Math.PI * 14 * (1 - animatedProgress / 100)}`,
                  transition: 'stroke-dashoffset 0.1s ease-out, stroke 0.3s ease'
                }}
              />
            </svg>
          </div>
        </div>

        {/* Expanded View */}
        <div className={`expanded-view ${isExpanded ? 'visible' : 'hidden'}`}>
          <div className="daily-title">Daily Goals!</div>

          {TIERS.map((tier) => {
            const isComplete = dailyClicks >= tier.limit;
            const barProgress = Math.min(displayedClicks / tier.limit, 1) * 100;
            return (
              <div key={tier.id} className={`tier-row ${isComplete ? 'tier-row-complete' : ''}`}>
                <div className="tier-row-header">
                  <span className="tier-row-icon">{tier.icon}</span>
                  <span className="tier-row-name" style={{ color: isComplete ? '#4ade80' : tier.ringColor }}>
                    {tier.name}
                  </span>
                  <span className="tier-row-count">
                    {isComplete
                      ? <span className="tier-checkmark">✓ Done</span>
                      : `${Math.floor(displayedClicks).toLocaleString()} / ${tier.limit.toLocaleString()}`
                    }
                  </span>
                </div>
                <div className="tier-bar-bg">
                  <div
                    className={`tier-bar-fill ${isComplete ? 'tier-bar-complete' : ''}`}
                    style={{
                      width: `${barProgress}%`,
                      background: isComplete
                        ? '#4ade80'
                        : `linear-gradient(90deg, ${tier.color}, ${tier.ringColor})`
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Daily Streak Display — clickable to share */}
      {(currentStreak > 0 || streakFreezes > 0) && (
        <div className="daily-streak-badge clickable" onClick={handleStreakShare} title="Tap to share your streak">
          <span className="streak-fire-container">
            🔥
            <span className="streak-number">{currentStreak}</span>
          </span>
          <span className="streak-label">streak</span>
          <span className="streak-full-text">
            {currentStreak} day{currentStreak !== 1 ? 's' : ''} in a row!
          </span>
          {streakFreezes > 0 && (
            <span className="streak-freeze-indicator" title={`${streakFreezes} freeze(s) available`}>
              🧊 {streakFreezes}
            </span>
          )}
          {perfectWeeks > 0 && (
            <span className="perfect-weeks-indicator" title={`${perfectWeeks} perfect week${perfectWeeks !== 1 ? 's' : ''} completed`}>
              🏆 {perfectWeeks}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default DailyProgressTracker;
